// Vistoria do veículo no app (pedido 2026-10-08: "menu novo em perfil para
// ele [motorista] fazer vistoria do carro"). Perfil → Vistoria: lista das
// vistorias que ele já fez + "Nova vistoria" (veículo, km, combustível,
// checklist, fotos, observações). Fotos sobem uma a uma logo depois de
// gravar a vistoria — ver vehicle-inspections.service.ts no backend.
// Itens/fotos vêm do checklist da empresa (Gestão de Frota → Vistorias →
// Checklist); item pode pedir foto própria (ex.: cada um dos 4 pneus).
// Sem internet (2026-10-16): grava no celular e envia depois sozinha — ver
// lib/offline-inspections.ts. Checklist e veículos ficam guardados no
// aparelho pra o formulário abrir mesmo offline.
import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  CloudOff,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { Vehicle } from "@/types";
import { compressImageFile } from "@/lib/compress-image";
import { stampImageFile } from "@/lib/stamp-image";
import { reverseGeocode, useReverseGeocode } from "@/hooks/use-reverse-geocode";
import { GpsDriveApiError, resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";
import { useMyVehicleInspections, useVehicleInspectionTemplate } from "@/hooks/queries/vehicle-inspections";
import { useAuth } from "@/contexts/auth-context";
import {
  discardPendingInspection,
  savePendingInspection,
  sendPendingInspection,
  syncPendingInspections,
  usePendingInspections,
  type PendingInspection,
} from "@/lib/offline-inspections";
import {
  DRIVER_SELFIE_PHOTO,
  FUEL_LEVEL_LABELS,
  MAX_INSPECTION_PHOTOS,
  vehicleInspectionsService,
  type FuelLevel,
  type InspectionChecklistItem,
  type InspectionItemStatus,
  type InspectionPhoto,
  type InspectionTemplate,
  type VehicleInspection,
} from "@/services/gpsdrive-vehicle-inspections.service";
import { LicensePlate } from "@/components/license-plate";

type View = { name: "list" } | { name: "new" } | { name: "detail"; inspection: VehicleInspection };

export function VehicleInspectionScreen({
  vehicles,
  onClose,
}: {
  vehicles: Vehicle[];
  onClose: () => void;
}) {
  const [view, setView] = useState<View>({ name: "list" });
  // Sem internet a frota não carrega — usa a última lista guardada.
  const offlineVehicles = useMemo(() => {
    const own = vehicles.filter((v) => !v.isTag);
    if (own.length > 0) {
      writeCache(VEHICLES_CACHE_KEY, own.map(({ id, name, plate, lat, lng, odometer }) => ({ id, name, plate, lat, lng, odometer })));
      return vehicles;
    }
    return (readCache<Vehicle[]>(VEHICLES_CACHE_KEY) ?? []).map((v) => ({ ...v, isTag: false }) as Vehicle);
  }, [vehicles]);
  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-slate-50 dark:bg-slate-950">
      {view.name === "list" && (
        <InspectionList
          onClose={onClose}
          onNew={() => setView({ name: "new" })}
          onOpen={(inspection) => setView({ name: "detail", inspection })}
        />
      )}
      {view.name === "new" && (
        <InspectionForm
          vehicles={offlineVehicles}
          onBack={() => setView({ name: "list" })}
          onDone={(inspection) => setView({ name: "detail", inspection })}
        />
      )}
      {view.name === "detail" && (
        <InspectionDetail inspection={view.inspection} onBack={() => setView({ name: "list" })} />
      )}
    </div>
  );
}

const VEHICLES_CACHE_KEY = "gpsdrive.inspection.vehicles.v1";
const TEMPLATE_CACHE_KEY = "gpsdrive.inspection.template.v1";

function readCache<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeCache(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // cota cheia/navegação privada — segue sem guardar
  }
}

// Endereço pro carimbo: sem internet não espera — usa as coordenadas.
async function addressForStamp(v: Vehicle): Promise<string> {
  const coords = `${v.lat.toFixed(6)}, ${v.lng.toFixed(6)}`;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return coords;
  const timeout = new Promise<null>((r) => setTimeout(() => r(null), 4000));
  const found = await Promise.race([reverseGeocode(v.lat, v.lng, true, v.id).catch(() => null), timeout]);
  return found ?? coords;
}

function Header({
  title,
  onBack,
  back = false,
}: {
  title: string;
  onBack: () => void;
  back?: boolean;
}) {
  return (
    <div
      className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 pb-3 dark:border-slate-800 dark:bg-slate-900"
      style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}
    >
      <button
        type="button"
        onClick={onBack}
        className="grid h-9 w-9 place-items-center rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
        aria-label={back ? "Voltar" : "Fechar"}
      >
        {back ? <ChevronLeft className="h-5 w-5" /> : <X className="h-5 w-5" />}
      </button>
      <div className="text-[15px] font-bold text-slate-900 dark:text-slate-100">{title}</div>
    </div>
  );
}

const dateTimeFmt = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

// ---- lista -----------------------------------------------------------------

function InspectionList({
  onClose,
  onNew,
  onOpen,
}: {
  onClose: () => void;
  onNew: () => void;
  onOpen: (i: VehicleInspection) => void;
}) {
  const { data = [], isLoading, isError } = useMyVehicleInspections();
  const { gpsDriveUser } = useAuth();
  const pending = usePendingInspections(gpsDriveUser?.id);
  const queryClient = useQueryClient();
  const [sending, setSending] = useState(false);
  const sendNow = async () => {
    if (!gpsDriveUser) return;
    setSending(true);
    try {
      const n = await syncPendingInspections(gpsDriveUser.id);
      if (n > 0) {
        toast.success(n === 1 ? "Vistoria enviada" : `${n} vistorias enviadas`);
        void queryClient.invalidateQueries({ queryKey: ["vehicle-inspections"] });
      } else {
        toast.info("Ainda sem internet — a vistoria continua guardada no celular.");
      }
    } finally {
      setSending(false);
    }
  };
  return (
    <>
      <Header title="Vistoria do veículo" onBack={onClose} />
      <div className="flex-1 overflow-y-auto px-4 pb-28 pt-4">
        <button
          type="button"
          onClick={onNew}
          className="flex w-full items-center gap-3 rounded-2xl bg-primary px-4 py-4 text-left text-primary-foreground shadow-sm active:opacity-90"
        >
          <span className="grid h-11 w-11 place-items-center rounded-full bg-white/20">
            <ClipboardCheck className="h-6 w-6" />
          </span>
          <span className="flex-1">
            <span className="block text-sm font-bold">Nova vistoria</span>
            <span className="block text-[12px] opacity-80">Confira o veículo e tire as fotos</span>
          </span>
          <Plus className="h-5 w-5" />
        </button>

        {pending.length > 0 && (
          <>
            <div className="mb-2 mt-6 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                Aguardando envio ({pending.length})
              </span>
              <button
                type="button"
                onClick={() => void sendNow()}
                disabled={sending}
                className="flex items-center gap-1 rounded-full bg-amber-500 px-3 py-1 text-[12px] font-semibold text-white disabled:opacity-60"
              >
                <RefreshCw className={cn("h-3.5 w-3.5", sending && "animate-spin")} />
                Enviar agora
              </button>
            </div>
            <div className="space-y-2">
              {pending.map((p) => (
                <PendingCard key={p.localId} p={p} />
              ))}
            </div>
          </>
        )}

        <div className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Minhas vistorias
        </div>
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : isError ? (
          <p className="py-6 text-center text-sm text-slate-500">Não foi possível carregar agora.</p>
        ) : data.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-500 dark:text-slate-400">
            Você ainda não fez nenhuma vistoria.
          </p>
        ) : (
          <div className="space-y-2">
            {data.map((i) => (
              <button
                key={i.id}
                type="button"
                onClick={() => onOpen(i)}
                className="flex w-full items-center gap-3 rounded-xl bg-white px-4 py-3 text-left shadow-sm dark:bg-slate-900"
              >
                <span
                  className={cn(
                    "grid h-9 w-9 shrink-0 place-items-center rounded-full",
                    i.hasIssues
                      ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                      : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
                  )}
                >
                  {i.hasIssues ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {i.vehicleName}
                    {i.plate && <LicensePlate plate={i.plate} size="xs" className="ml-1.5" />}
                  </span>
                  <span className="block text-[12px] text-slate-500 dark:text-slate-400">
                    {dateTimeFmt.format(new Date(i.performedAt ?? i.createdAt))}
                    {i.hasIssues ? " · com problemas" : " · tudo ok"}
                  </span>
                </span>
                <ChevronRight className="h-4 w-4 text-slate-400" />
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function PendingCard({ p }: { p: PendingInspection }) {
  const [confirm, setConfirm] = useState(false);
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-950/30">
      <div className="flex items-center gap-3">
        <CloudOff className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
            {p.vehicleName}
            {p.plate && <LicensePlate plate={p.plate} size="xs" className="ml-1.5" />}
          </div>
          <div className="text-[12px] text-slate-600 dark:text-slate-300">
            Feita em {dateTimeFmt.format(new Date(p.performedAt))}
            {p.photos.length > 0 ? ` · ${p.photos.length} foto(s) a enviar` : ""}
          </div>
        </div>
      </div>
      {p.rejected && (
        <div className="mt-2 flex items-center gap-2 text-[12px]">
          <span className="flex-1 text-rose-600 dark:text-rose-400">Não aceita: {p.rejected}</span>
          {confirm ? (
            <>
              <button
                type="button"
                onClick={() => void discardPendingInspection(p.localId)}
                className="rounded-md bg-rose-600 px-2 py-1 font-semibold text-white"
              >
                Descartar
              </button>
              <button type="button" onClick={() => setConfirm(false)} className="px-1 text-slate-500">
                Cancelar
              </button>
            </>
          ) : (
            <button type="button" onClick={() => setConfirm(true)} className="text-slate-500" aria-label="Descartar">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---- formulário --------------------------------------------------------------

interface PhotoDraft {
  id: string;
  // "item:<key>", "slot:<key>" ou "extra" — uma foto por item/posição.
  slot: string;
  label: string;
  file: File;
  preview: string;
  // Hora em que a foto foi tirada — vai carimbada na imagem.
  takenAt: Date;
}

export const STATUS_BUTTONS: { value: InspectionItemStatus; label: string; active: string }[] = [
  { value: "ok", label: "OK", active: "bg-emerald-600 text-white" },
  { value: "problema", label: "Problema", active: "bg-amber-500 text-white" },
  { value: "na", label: "N/A", active: "bg-slate-500 text-white" },
];

function InspectionForm(props: {
  vehicles: Vehicle[];
  onBack: () => void;
  onDone: (i: VehicleInspection) => void;
}) {
  const { data: fresh, isLoading, isError, refetch } = useVehicleInspectionTemplate();
  useEffect(() => {
    if (fresh) writeCache(TEMPLATE_CACHE_KEY, fresh);
  }, [fresh]);
  // Sem internet: último checklist guardado no celular.
  const template = fresh ?? (isError ? readCache<InspectionTemplate>(TEMPLATE_CACHE_KEY) : null);
  if (!template) {
    return (
      <>
        <Header title="Nova vistoria" onBack={props.onBack} back />
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
          {isLoading ? (
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          ) : isError ? (
            <>
              <p className="text-sm text-slate-500">Não foi possível carregar o checklist.</p>
              <button
                type="button"
                onClick={() => void refetch()}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
              >
                Tentar de novo
              </button>
            </>
          ) : null}
        </div>
      </>
    );
  }
  return <InspectionFormBody {...props} template={template} />;
}

function InspectionFormBody({
  vehicles,
  onBack,
  onDone,
  template,
}: {
  vehicles: Vehicle[];
  onBack: () => void;
  onDone: (i: VehicleInspection) => void;
  template: InspectionTemplate;
}) {
  const queryClient = useQueryClient();
  const { gpsDriveUser } = useAuth();
  const options = useMemo(() => vehicles.filter((v) => !v.isTag), [vehicles]);
  const [vehicleId, setVehicleId] = useState<string>(options.length === 1 ? options[0].id : "");
  const vehicle = options.find((v) => v.id === vehicleId) ?? null;
  const [odometer, setOdometer] = useState("");
  const [fuel, setFuel] = useState<FuelLevel | null>(null);
  const [items, setItems] = useState<InspectionChecklistItem[]>(() =>
    template.items.map((i) => ({ key: i.key, label: i.label, status: "ok" as const })),
  );
  const photoModeByKey = useMemo(() => new Map(template.items.map((i) => [i.key, i.photo])), [template]);
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState<PhotoDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Foto ponto abre a câmera da frente (selfie com a placa).
  const selfieInputRef = useRef<HTMLInputElement>(null);
  const selfieSlot = template.photos.find((p) => p.key === DRIVER_SELFIE_PHOTO.key) ?? null;
  const vehicleSlots = template.photos.filter((p) => p.key !== DRIVER_SELFIE_PHOTO.key);
  const pendingRef = useRef({ slot: "", label: "" });

  const setItem = (key: string, patch: Partial<InspectionChecklistItem>) =>
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ...patch } : i)));

  const pickPhoto = (slot: string, label: string) => {
    pendingRef.current = { slot, label };
    (slot === `slot:${DRIVER_SELFIE_PHOTO.key}` ? selfieInputRef : fileInputRef).current?.click();
  };
  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const { slot, label } = pendingRef.current;
    setPhotos((prev) => [
      // O mesmo item/posição (Frente, Pneu…) só guarda a foto mais recente.
      ...prev.filter((p) => slot === "extra" || p.slot !== slot),
      { id: crypto.randomUUID(), slot, label, file, preview: URL.createObjectURL(file), takenAt: new Date() },
    ]);
  };
  const removePhoto = (id: string) => setPhotos((prev) => prev.filter((p) => p.id !== id));

  const submit = async () => {
    setError(null);
    if (!vehicle) {
      setError("Escolha o veículo.");
      return;
    }
    if (template.requireOdometer && !odometer.trim()) {
      setError("Informe o km do painel.");
      return;
    }
    if (template.requireFuel && !fuel) {
      setError("Informe o nível de combustível.");
      return;
    }
    const missingNote = items.find((i) => i.status === "problema" && !i.note?.trim());
    if (missingNote) {
      setError(`Descreva o problema em "${missingNote.label}".`);
      return;
    }
    const missingItemPhoto = items.find(
      (i) => i.status !== "na" && photoModeByKey.get(i.key) === "required" && !photoFor(`item:${i.key}`),
    );
    if (missingItemPhoto) {
      setError(`Tire a foto de "${missingItemPhoto.label}".`);
      return;
    }
    const missingSlot = template.photos.find((p) => p.required && !photoFor(`slot:${p.key}`));
    if (missingSlot) {
      setError(`Tire a foto "${missingSlot.label}".`);
      return;
    }
    if (!gpsDriveUser) {
      setError("Sessão não carregada — feche e abra a vistoria de novo.");
      return;
    }
    setSaving(true);
    const performedAt = new Date();
    const localId = crypto.randomUUID();
    try {
      // 1. Carimbo nas fotos (data/hora + veículo + endereço; sem internet,
      //    coordenadas) e grava tudo no celular ANTES de enviar.
      let address = "";
      if (photos.length > 0 && Number.isFinite(vehicle.lat) && Number.isFinite(vehicle.lng)) {
        setProgress("Preparando fotos...");
        address = await addressForStamp(vehicle);
      }
      const stamped = await Promise.all(
        photos.map(async (p) => {
          const file =
            (await stampImageFile(p.file, [
              `${dateTimeFmt.format(p.takenAt)} · ${vehicle.name}${vehicle.plate && vehicle.plate !== vehicle.name ? ` · ${vehicle.plate}` : ""}`,
              address,
            ])) ?? (await compressImageFile(p.file));
          return { id: p.id, label: p.label, blob: file as Blob, name: file.name || "foto.jpg" };
        }),
      );
      const checklist = items.map((i) => (i.status === "problema" ? i : { key: i.key, label: i.label, status: i.status }));
      await savePendingInspection({
        localId,
        userId: gpsDriveUser.id,
        performedAt: performedAt.toISOString(),
        vehicleName: vehicle.name,
        plate: vehicle.plate && vehicle.plate !== vehicle.name ? vehicle.plate : null,
        hasIssues: checklist.some((i) => i.status === "problema"),
        input: {
          traccarDeviceId: Number(vehicle.id),
          odometerKm: odometer.trim() ? Math.round(Number(odometer.replace(/\D/g, ""))) : null,
          fuelLevel: fuel,
          checklist,
          notes: notes.trim() || null,
          latitude: Number.isFinite(vehicle.lat) ? vehicle.lat : null,
          longitude: Number.isFinite(vehicle.lng) ? vehicle.lng : null,
        },
        photos: stamped,
        serverId: null,
        uploadedPhotos: 0,
        rejected: null,
      });

      // 2. Tenta enviar agora.
      setProgress(photos.length > 0 ? `Enviando vistoria e ${photos.length} foto(s)...` : "Enviando vistoria...");
      const result = await sendPendingInspection(localId);
      if (result.status === "sent") {
        void queryClient.invalidateQueries({ queryKey: ["vehicle-inspections"] });
        if (result.failedPhotos > 0) toast.warning(`Vistoria salva, mas ${result.failedPhotos} foto(s) não foram aceitas.`);
        onDone(result.inspection);
      } else if (result.status === "queued") {
        toast.info("Sem internet — vistoria guardada no celular", {
          description: "Ela será enviada sozinha quando a internet voltar.",
        });
        onBack();
      } else {
        // Recusada (ex.: veículo desvinculado) — sai da fila, o motorista corrige aqui.
        await discardPendingInspection(localId);
        setError(result.message);
      }
    } catch (err) {
      setError(err instanceof GpsDriveApiError ? err.message : "Não foi possível guardar a vistoria. Tente de novo.");
    } finally {
      setSaving(false);
      setProgress("");
    }
  };

  function photoFor(slot: string) {
    return photos.find((p) => p.slot === slot);
  }
  const extras = photos.filter((p) => p.slot === "extra");

  return (
    <>
      <Header title="Nova vistoria" onBack={onBack} back />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onFile}
      />
      <input ref={selfieInputRef} type="file" accept="image/*" capture="user" className="hidden" onChange={onFile} />
      <div className="flex-1 space-y-4 overflow-y-auto px-4 pb-32 pt-4">
        {/* Veículo */}
        <Section title="Veículo">
          {options.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum veículo vinculado à sua conta.</p>
          ) : (
            <select
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
              className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            >
              <option value="">Selecione o veículo</option>
              {options.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                  {v.plate ? ` · ${v.plate}` : ""}
                </option>
              ))}
            </select>
          )}
        </Section>

        {/* Km + combustível */}
        <Section title="Quilometragem e combustível">
          <label className="block text-[12px] font-medium text-slate-500 dark:text-slate-400">
            Km do painel{template.requireOdometer ? " *" : ""}
            <input
              inputMode="numeric"
              value={odometer}
              onChange={(e) => setOdometer(e.target.value.replace(/\D/g, ""))}
              placeholder={
                vehicle?.odometer ? `Rastreador: ${Math.round(vehicle.odometer).toLocaleString("pt-BR")}` : "Ex.: 45230"
              }
              className="mt-1 h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            />
          </label>
          <div className="mt-3 text-[12px] font-medium text-slate-500 dark:text-slate-400">
            Combustível{template.requireFuel ? " *" : ""}
          </div>
          <div className="mt-1 grid grid-cols-5 gap-1">
            {(Object.keys(FUEL_LEVEL_LABELS) as FuelLevel[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFuel(fuel === f ? null : f)}
                className={cn(
                  "h-10 rounded-lg text-[12px] font-semibold",
                  fuel === f
                    ? "bg-primary text-primary-foreground"
                    : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
                )}
              >
                {FUEL_LEVEL_LABELS[f]}
              </button>
            ))}
          </div>
        </Section>

        {/* Foto ponto */}
        {selfieSlot && (
          <Section title="Foto ponto *">
            <div className="flex items-center gap-3">
              <div className="w-28 shrink-0">
                <PhotoTile
                  label="Selfie"
                  preview={photoFor(`slot:${selfieSlot.key}`)?.preview}
                  onPick={() => pickPhoto(`slot:${selfieSlot.key}`, selfieSlot.label)}
                  onRemove={(() => {
                    const p = photoFor(`slot:${selfieSlot.key}`);
                    return p ? () => removePhoto(p.id) : undefined;
                  })()}
                />
              </div>
              <p className="text-[13px] leading-snug text-slate-600 dark:text-slate-300">
                Tire uma foto sua <b>mostrando a placa do veículo</b>. A câmera da frente abre sozinha.
              </p>
            </div>
          </Section>
        )}

        {/* Checklist */}
        <Section title="Checklist">
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {items.map((item) => {
              const photoMode = photoModeByKey.get(item.key) ?? "none";
              const itemPhoto = photoFor(`item:${item.key}`);
              return (
              <div key={item.key} className="py-2.5">
                <div className="flex items-center gap-2">
                  <div className="flex-1 text-[13px] text-slate-800 dark:text-slate-100">
                    {item.label}
                    {photoMode === "required" && item.status !== "na" && (
                      <span className="ml-1 text-[11px] font-semibold text-rose-500">foto *</span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {STATUS_BUTTONS.map((b) => (
                      <button
                        key={b.value}
                        type="button"
                        onClick={() => setItem(item.key, { status: b.value })}
                        className={cn(
                          "h-8 rounded-md px-2 text-[11px] font-semibold",
                          item.status === b.value
                            ? b.active
                            : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
                        )}
                      >
                        {b.label}
                      </button>
                    ))}
                  </div>
                </div>
                {item.status === "problema" && (
                  <input
                    value={item.note ?? ""}
                    onChange={(e) => setItem(item.key, { note: e.target.value })}
                    placeholder="O que está errado?"
                    className="mt-2 h-10 w-full rounded-lg border border-amber-300 bg-amber-50 px-3 text-sm text-slate-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-slate-100"
                  />
                )}
                {photoMode !== "none" && item.status !== "na" && (
                  <div className="mt-2 w-24">
                    <PhotoTile
                      label="Foto"
                      preview={itemPhoto?.preview}
                      onPick={() => pickPhoto(`item:${item.key}`, item.label)}
                      onRemove={itemPhoto ? () => removePhoto(itemPhoto.id) : undefined}
                    />
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </Section>

        {/* Fotos — sem "Fotos do veículo" no checklist da empresa, fica só a
            foto extra opcional (as fotos de item ficam no Checklist). */}
        <Section title={vehicleSlots.length > 0 ? "Fotos" : "Outra foto (opcional)"}>
          <div className="grid grid-cols-3 gap-2">
            {vehicleSlots.map((slot) => {
              const p = photoFor(`slot:${slot.key}`);
              return (
                <PhotoTile
                  key={slot.key}
                  label={slot.required ? `${slot.label} *` : slot.label}
                  preview={p?.preview}
                  onPick={() => pickPhoto(`slot:${slot.key}`, slot.label)}
                  onRemove={p ? () => removePhoto(p.id) : undefined}
                />
              );
            })}
            {extras.map((p, n) => (
              <PhotoTile
                key={p.id}
                label={`Extra ${n + 1}`}
                preview={p.preview}
                onPick={() => undefined}
                onRemove={() => removePhoto(p.id)}
              />
            ))}
            {photos.length < MAX_INSPECTION_PHOTOS && (
              <button
                type="button"
                onClick={() => pickPhoto("extra", "Extra")}
                className="flex aspect-square flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-slate-300 text-[11px] font-medium text-slate-500 dark:border-slate-700 dark:text-slate-400"
              >
                <Plus className="h-5 w-5" />
                Outra foto
              </button>
            )}
          </div>
        </Section>

        {/* Observações */}
        <Section title="Observações">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Algo mais que a empresa precisa saber?"
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
        </Section>

        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={saving || options.length === 0}
          className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-bold text-primary-foreground shadow-sm disabled:opacity-60"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {progress || "Enviando..."}
            </>
          ) : (
            "Enviar vistoria"
          )}
        </button>
      </div>
    </>
  );
}

export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl bg-white p-4 shadow-sm dark:bg-slate-900">
      <div className="mb-2 text-[13px] font-bold text-slate-900 dark:text-slate-100">{title}</div>
      {children}
    </div>
  );
}

export function PhotoTile({
  label,
  preview,
  onPick,
  onRemove,
}: {
  label: string;
  preview?: string;
  onPick: () => void;
  onRemove?: () => void;
}) {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onPick}
        className={cn(
          "flex aspect-square w-full flex-col items-center justify-center gap-1 overflow-hidden rounded-xl text-[11px] font-medium",
          preview
            ? "bg-slate-200 dark:bg-slate-800"
            : "border-2 border-dashed border-slate-300 text-slate-500 dark:border-slate-700 dark:text-slate-400",
        )}
      >
        {preview ? (
          <img src={preview} alt={label} className="h-full w-full object-cover" />
        ) : (
          <>
            <Camera className="h-5 w-5" />
            {label}
          </>
        )}
      </button>
      {preview && (
        <>
          <span className="pointer-events-none absolute inset-x-0 bottom-0 rounded-b-xl bg-black/50 px-1 py-0.5 text-center text-[10px] font-medium text-white">
            {label}
          </span>
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-black/60 text-white"
              aria-label={`Remover foto ${label}`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ---- detalhe -----------------------------------------------------------------

export function InspectionDetailBody({ inspection: i }: { inspection: VehicleInspection }) {
  const [zoom, setZoom] = useState<InspectionPhoto | null>(null);
  const problems = i.checklist.filter((c) => c.status === "problema");
  // Foto de item (pneu etc.) já aparece dentro do Checklist — embaixo só as
  // fotos gerais (Frente, Traseira, extras), sem repetir.
  const itemLabels = new Set(i.checklist.map((c) => c.label));
  const selfie = i.photos.find((p) => p.label === DRIVER_SELFIE_PHOTO.label) ?? null;
  const generalPhotos = i.photos.filter(
    (p) => p !== selfie && (!p.label || !itemLabels.has(p.label)),
  );
  const address = useReverseGeocode(i.latitude ?? undefined, i.longitude ?? undefined, {
    stableKey: String(i.traccarDeviceId),
  });
  return (
    <div className="space-y-4">
      <div
        className={cn(
          "flex items-center gap-3 rounded-2xl px-4 py-3",
          i.hasIssues
            ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
            : "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
        )}
      >
        {i.hasIssues ? <AlertTriangle className="h-5 w-5" /> : <CheckCircle2 className="h-5 w-5" />}
        <div className="text-sm font-semibold">
          {i.hasIssues ? `${problems.length} item(ns) com problema` : "Tudo ok"}
        </div>
      </div>

      <div className="rounded-2xl bg-white p-4 text-sm shadow-sm dark:bg-slate-900">
        <Row label="Veículo" value={`${i.vehicleName}${i.plate ? ` · ${i.plate}` : ""}`} />
        <Row label="Feita por" value={i.userName || "—"} />
        <Row label="Data" value={dateTimeFmt.format(new Date(i.performedAt ?? i.createdAt))} />
        {i.performedAt && new Date(i.createdAt).getTime() - new Date(i.performedAt).getTime() > 2 * 60_000 && (
          <Row label="Enviada (feita sem internet)" value={dateTimeFmt.format(new Date(i.createdAt))} />
        )}
        {address && <Row label="Endereço" value={address} />}
        <Row label="Km" value={i.odometerKm != null ? i.odometerKm.toLocaleString("pt-BR") : "—"} />
        <Row label="Combustível" value={i.fuelLevel ? FUEL_LEVEL_LABELS[i.fuelLevel] : "—"} />
        {i.latitude != null && i.longitude != null && (
          <a
            href={`https://www.google.com/maps?q=${i.latitude},${i.longitude}`}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-block text-[13px] font-medium text-primary underline"
          >
            Ver local no mapa
          </a>
        )}
      </div>

      {selfie && (
        <div className="rounded-2xl bg-white p-4 shadow-sm dark:bg-slate-900">
          <div className="mb-2 text-[13px] font-bold text-slate-900 dark:text-slate-100">Foto ponto</div>
          <button
            type="button"
            onClick={() => setZoom(selfie)}
            className="block h-40 w-32 overflow-hidden rounded-xl bg-slate-200 dark:bg-slate-800"
          >
            <img src={resolveGpsDriveUrl(selfie.thumbUrl)} alt="Foto ponto" className="h-full w-full object-cover" />
          </button>
        </div>
      )}

      <div className="rounded-2xl bg-white p-4 shadow-sm dark:bg-slate-900">
        <div className="mb-2 text-[13px] font-bold text-slate-900 dark:text-slate-100">Checklist</div>
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {i.checklist.map((c) => (
            <div key={c.key} className="py-2">
              <div className="flex items-center justify-between gap-2 text-[13px]">
                <span className="text-slate-800 dark:text-slate-100">{c.label}</span>
                <span
                  className={cn(
                    "rounded px-2 py-0.5 text-[11px] font-semibold",
                    c.status === "ok" && "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
                    c.status === "problema" && "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
                    c.status === "na" && "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
                  )}
                >
                  {c.status === "ok" ? "OK" : c.status === "problema" ? "Problema" : "N/A"}
                </span>
              </div>
              {c.note && <div className="mt-1 text-[12px] text-amber-700 dark:text-amber-300">{c.note}</div>}
              {i.photos.some((p) => p.label === c.label) && (
                <div className="mt-1.5 flex gap-1.5">
                  {i.photos
                    .filter((p) => p.label === c.label)
                    .map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setZoom(p)}
                        className="h-14 w-14 overflow-hidden rounded-lg bg-slate-200 dark:bg-slate-800"
                      >
                        <img src={resolveGpsDriveUrl(p.thumbUrl)} alt={c.label} className="h-full w-full object-cover" />
                      </button>
                    ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {i.notes && (
        <div className="rounded-2xl bg-white p-4 shadow-sm dark:bg-slate-900">
          <div className="mb-1 text-[13px] font-bold text-slate-900 dark:text-slate-100">Observações</div>
          <p className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{i.notes}</p>
        </div>
      )}

      {generalPhotos.length > 0 && (
        <div className="rounded-2xl bg-white p-4 shadow-sm dark:bg-slate-900">
          <div className="mb-2 text-[13px] font-bold text-slate-900 dark:text-slate-100">
            Fotos ({generalPhotos.length})
          </div>
          <div className="grid grid-cols-3 gap-2">
            {generalPhotos.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setZoom(p)}
                className="relative aspect-square overflow-hidden rounded-xl bg-slate-200 dark:bg-slate-800"
              >
                <img src={resolveGpsDriveUrl(p.thumbUrl)} alt={p.label ?? "Foto"} className="h-full w-full object-cover" />
                {p.label && (
                  <span className="absolute inset-x-0 bottom-0 bg-black/50 px-1 py-0.5 text-center text-[10px] text-white">
                    {p.label}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {zoom && (
        <button
          type="button"
          onClick={() => setZoom(null)}
          className="fixed inset-0 z-[1100] flex flex-col items-center justify-center gap-3 bg-black/90 p-4"
          aria-label="Fechar foto"
        >
          <img src={resolveGpsDriveUrl(zoom.url)} alt={zoom.label ?? "Foto da vistoria"} className="min-h-0 max-w-full flex-1 object-contain" />
          <div className="w-full max-w-xl text-left text-[13px] leading-snug text-white">
            {zoom.label && <div className="font-semibold">{zoom.label}</div>}
            <div>{dateTimeFmt.format(new Date(zoom.createdAt))}</div>
            {address && <div className="text-white/80">{address}</div>}
          </div>
        </button>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 py-1">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className="text-right font-medium text-slate-900 dark:text-slate-100">{value}</span>
    </div>
  );
}

function InspectionDetail({ inspection, onBack }: { inspection: VehicleInspection; onBack: () => void }) {
  return (
    <>
      <Header title="Vistoria" onBack={onBack} back />
      <div className="flex-1 overflow-y-auto px-4 pb-28 pt-4">
        <InspectionDetailBody inspection={inspection} />
      </div>
    </>
  );
}
